import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import ConfigTable from "../../../Common/Tables/ConfigTable";
import {
  Button,
  PageHeading,
  Icon,
  ConfirmDialog,
  employeeID,
  companyID,
} from "../../../Common/UIComponents";
import {
  fetchLocations,
  deleteLocation,
  deleteTwoColumnListEntry,
} from "../../../API_Service/Settings_Slice";
import { URL } from "../../../Common/APIURL";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const buildTableRows = (items = []) =>
  items.map((item, i) => ({
    id:          item.City_Id          ?? item.ID       ?? `row-${i}`,
    country:     item.Country_Name     ?? item.Country  ?? "—",
    countryCode: item.Country_Code     ?? item.CountryCode ?? "",
    state:       item.State_Name       ?? item.State    ?? "—",
    city:        item.City_Name        ?? item.City     ?? "—",
    iconPath:    item.LocationIconPath ?? item.IconPath ?? "",
  }));

const EXPORT_COLUMNS = [
  { key: "country", title: "Country"        },
  { key: "state",   title: "State / Region" },
  { key: "city",    title: "City"           },
];

// ─────────────────────────────────────────────
// COUNTRY FLAG HELPER
// Converts ISO 3166-1 alpha-2 country code (e.g. "US", "IN", "GB")
// to the corresponding flag emoji using regional indicator symbols.
// Falls back to a globe icon span if code is missing/invalid.
// ─────────────────────────────────────────────

const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return null;
  const code = countryCode.toUpperCase();
  // Each letter A-Z maps to regional indicator symbol 🇦(U+1F1E6) + offset
  return String.fromCodePoint(
    ...code.split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ rows }) => {
  const total     = rows.length;
  const regions   = useMemo(() => new Set(rows.map((r) => r.state)).size,   [rows]);
  const countries = useMemo(() => new Set(rows.map((r) => r.country)).size, [rows]);

  const stats = [
    { label: "Total Locations", value: total,     icon: "public",    bg: "bg-[#005b60]/10", color: "text-[#005b60]" },
    { label: "Active Regions",  value: regions,   icon: "map",       bg: "bg-[#8fc444]/10", color: "text-[#436900]" },
    { label: "Countries",       value: countries, icon: "analytics", bg: "bg-[#0088cc]/10", color: "text-[#0088cc]" },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      {stats.map((s) => (
        <div key={s.label} className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center ${s.color} flex-shrink-0`}>
            <span className="material-symbols-outlined text-[22px]">{s.icon}</span>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">{s.label}</p>
            <p className="text-xl font-bold text-slate-900 leading-tight">{total === 0 ? "—" : s.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function LocationConfigIndex({ embedded = false } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [locations,     setLocations]     = useState([]);
  const [isLoading,     setIsLoading]     = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError,   setDeleteError]   = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [editSuccess,   setEditSuccess]   = useState(false);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [deleteDialog,  setDeleteDialog]  = useState({ open: false, row: null });
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  // ── Fetch ─────────────────────────────────────
  const fetchList = useCallback(() => {
    setIsLoading(true);
    setSettingsError(null);
    dispatch(fetchLocations({ ID: 0, companyID }))
      .then((res) => setLocations(toArray(res)))
      .catch((err) => setSettingsError(err?.message ?? "Failed to load locations"))
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchList(); }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.refresh) {
      fetchList();
      if (location.state?.editSuccess) {
        setEditSuccess(true);
        setTimeout(() => setEditSuccess(false), 3000);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => buildTableRows(locations), [locations]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.country.toLowerCase().includes(q) ||
        r.state.toLowerCase().includes(q)   ||
        r.city.toLowerCase().includes(q)
    );
  }, [searchQuery, rows]);

  const totalPages    = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // ── Handlers ─────────────────────────────────
  const handleEdit = useCallback(
    (row) => navigate("/application-config/location/create", { state: { mode: "edit", data: row } }),
    [navigate]
  );

  const handleDelete       = useCallback((row) => setDeleteDialog({ open: true, row }), []);
  const handleCancelDelete = useCallback(() => { setDeleteDialog({ open: false, row: null }); setDeleteBlocked(false); }, []);

  const handleConfirmDelete = useCallback(() => {
    const { row } = deleteDialog;
    if (!row) return;
    setDeleteLoading(true);
    setDeleteError(null);
    dispatch(deleteTwoColumnListEntry({ term: "LOCATION", id: row.id, modifiedBy: employeeID, companyID }))
    // dispatch(deleteLocation({ id: row.id, modifiedBy: employeeID, companyID }))
      .then((action) => {
        const result = action?.payload?.data;
        if (Number(result) === -1) {
          setDeleteBlocked(true);
          return;
        }
        setDeleteBlocked(false);
        setDeleteDialog({ open: false, row: null });
        setDeleteSuccess(true);
        fetchList();
        setTimeout(() => setDeleteSuccess(false), 3000);
      })
      .catch((err) => setDeleteError(err?.message ?? "Failed to delete"))
      .finally(() => setDeleteLoading(false));
  }, [deleteDialog, dispatch, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddNew = useCallback(
    () => navigate("/application-config/location/create"),
    [navigate]
  );

  const handleSearchChange   = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);

  // ── Columns ───────────────────────────────────
  const columns = useMemo(() => [
    {
      key:   "country",
      title: "Country",
      render: (row) => {
        const flag = getFlagEmoji(row.countryCode);
        return (
          <div className="flex items-center gap-3">
            {/* Flag emoji — same as the HTML reference */}
            {flag ? (
              <span className="text-2xl leading-none select-none">{flag}</span>
            ) : (
              /* Fallback globe icon when no country code available */
              <div className="w-8 h-8 rounded-lg bg-[#005b60]/10 flex items-center justify-center text-[#005b60] flex-shrink-0">
                <span className="material-symbols-outlined text-[18px]">public</span>
              </div>
            )}
            <span className="font-semibold text-[#181c1d]">{row.country}</span>
          </div>
        );
      },
    },
    {
      key:   "state",
      title: "State / Region",
      render: (row) => <span className="text-slate-600">{row.state}</span>,
    },
    {
      key:   "city",
      title: "City",
      render: (row) => <span className="font-medium text-slate-800">{row.city}</span>,
    },
    {
      key:   "cityIcon",
      title: "City Icon/Image",
      render: (row) => {
        if (!row.iconPath) {
          return (
            <div className="w-10 h-10 rounded-lg bg-[#005b60]/10 flex items-center justify-center text-[#005b60]">
              <span className="material-symbols-outlined text-[20px]">location_on</span>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <img
              src={`${URL}${row.iconPath}`}
              alt={row.city}
              className="w-10 h-10 rounded-lg object-cover border border-[#bdc9c9]/40 flex-shrink-0"
              onError={(e) => {
                // If the image fails, swap to fallback icon
                e.target.style.display = "none";
                const fallback = e.target.nextSibling;
                if (fallback) fallback.style.display = "flex";
              }}
            />
            {/* Fallback — hidden by default, shown on img error */}
            <div
              className="w-10 h-10 rounded-lg bg-[#005b60]/10 items-center justify-center text-[#005b60] flex-shrink-0"
              style={{ display: "none" }}
            >
              <span className="material-symbols-outlined text-[20px]">location_on</span>
            </div>
          </div>
        );
      },
    },
  ], []);

  const inner = (
    <>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d] min-h-screen">


        {/* Page Header */}
        <div className="flex flex-wrap justify-between items-end mb-3 gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-[#08757C] tracking-tight">
              Location Management
            </h1>
            <p className="text-slate-500 mt-1 text-sm font-medium">
              Centralized reference for system-defined geographic hierarchies.
            </p>
          </div>
          <Button variant="primary" onClick={handleAddNew}>
            <Icon name="add" className="mr-1 text-[20px]" />
            Add Location
          </Button>
        </div>

        {/* Stats */}
        <StatsRow rows={rows} />

        {/* Table */}
        <ConfigTable
          persistKey="application-config-location"
          columns={columns}
          rows={isLoading ? [] : paginatedRows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by Country, State, or City..."
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="locations"
          emptyText={isLoading ? "Loading…" : "No locations found."}
        />

        {settingsError && (
          <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">{settingsError}</div>
        )}
      </div>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        isLoading={deleteLoading}
        errorMessage={deleteBlocked ? "This location is already mapped with another module. Please unmap it before deleting." : null}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Delete Success Toast */}
      {deleteSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          Location Deleted successfully.
        </div>
      )}

      {/* Edit Success Toast */}
      {editSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          Location Updated successfully.
        </div>
      )}

      {/* Delete Error Toast */}
      {deleteError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-xl shadow-lg">
          {deleteError}
        </div>
      )}
    </>
  );
  return embedded ? inner : <DashboardLayout>{inner}</DashboardLayout>;
}

